// Anchored popover for ARBITRARY panel content (forms, pickers, builders) — the
// sibling of @archlang/ui's `Menu` (which is for menuitem lists), built on the
// same @floating-ui/react stack: click to open, portal to <body> (escapes any
// `overflow-hidden` ancestor clip), flip/shift positioning, Esc + outside-press
// dismiss.
//
// Crucially there is NO full-screen scrim: dismiss is a document pointer
// listener, so the surface underneath (the board) keeps receiving wheel/zoom
// while the popover is open. A scrim overlay would swallow those events.
//
//   <Popover open={open} onOpenChange={setOpen} placement="bottom-end">
//     <PopoverTrigger>
//       {({ ref, props, open }) => <HudChip ref={ref} {...props} active={open}>…</HudChip>}
//     </PopoverTrigger>
//     <PopoverPanel className="w-96 …">{content}</PopoverPanel>
//   </Popover>
//
// (Lives in app-shell rather than @archlang/ui only because a new shared-package
// export can't be resolved from a git worktree until it lands on trunk; it
// belongs in the design system and can graduate there later.)
import {
  createContext,
  useContext,
  useState,
  type HTMLAttributes,
  type ReactNode,
  type Ref,
} from "react";
import {
  FloatingFocusManager,
  FloatingPortal,
  autoUpdate,
  flip,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
  type Placement,
} from "@floating-ui/react";

interface PopoverContextValue {
  readonly open: boolean;
  readonly refs: ReturnType<typeof useFloating>["refs"];
  readonly floatingStyles: ReturnType<typeof useFloating>["floatingStyles"];
  readonly context: ReturnType<typeof useFloating>["context"];
  readonly isPositioned: boolean;
  readonly getReferenceProps: ReturnType<typeof useInteractions>["getReferenceProps"];
  readonly getFloatingProps: ReturnType<typeof useInteractions>["getFloatingProps"];
}

const PopoverCtx = createContext<PopoverContextValue | null>(null);

function usePopoverCtx(name: string): PopoverContextValue {
  const ctx = useContext(PopoverCtx);
  if (!ctx) throw new Error(`<${name}> must be used inside <Popover>`);
  return ctx;
}

export function Popover({
  children,
  placement = "bottom-end",
  open: openProp,
  onOpenChange,
  offsetPx = 6,
}: {
  readonly children: ReactNode;
  readonly placement?: Placement;
  /** Controlled open state. Omit for uncontrolled (internal) state. */
  readonly open?: boolean;
  readonly onOpenChange?: (open: boolean) => void;
  readonly offsetPx?: number;
}) {
  const [internalOpen, setInternalOpen] = useState(false);
  const open = openProp ?? internalOpen;
  const setOpen = (v: boolean): void => {
    if (openProp === undefined) setInternalOpen(v);
    onOpenChange?.(v);
  };

  const { refs, floatingStyles, context, isPositioned } = useFloating({
    open,
    onOpenChange: setOpen,
    placement,
    whileElementsMounted: autoUpdate,
    middleware: [offset(offsetPx), flip({ padding: 8 }), shift({ padding: 8 })],
  });

  const click = useClick(context, { event: "click" });
  const dismiss = useDismiss(context, { escapeKey: true, outsidePress: true });
  const role = useRole(context, { role: "dialog" });
  const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss, role]);

  return (
    <PopoverCtx.Provider
      value={{ open, refs, floatingStyles, context, isPositioned, getReferenceProps, getFloatingProps }}
    >
      {children}
    </PopoverCtx.Provider>
  );
}

/** Render-prop trigger — attach `ref` + `props` to your own control (a button,
 *  HudChip, …). `open` reflects the popover state for active styling. */
export function PopoverTrigger({
  children,
}: {
  readonly children: (args: {
    readonly ref: Ref<HTMLButtonElement>;
    readonly props: Record<string, unknown>;
    readonly open: boolean;
  }) => ReactNode;
}): ReactNode {
  const { refs, getReferenceProps, open } = usePopoverCtx("PopoverTrigger");
  return children({
    ref: refs.setReference as Ref<HTMLButtonElement>,
    props: { ...getReferenceProps(), "aria-expanded": open },
    open,
  });
}

export function PopoverPanel({
  children,
  className,
  ...rest
}: {
  readonly children: ReactNode;
  readonly className?: string;
} & Omit<HTMLAttributes<HTMLDivElement>, "children">): ReactNode {
  const { open, refs, floatingStyles, context, isPositioned, getFloatingProps } =
    usePopoverCtx("PopoverPanel");
  if (!open) return null;
  return (
    <FloatingPortal>
      <FloatingFocusManager context={context} modal={false} initialFocus={-1}>
        <div
          ref={refs.setFloating}
          style={{
            ...floatingStyles,
            opacity: isPositioned ? 1 : 0,
            pointerEvents: isPositioned ? undefined : "none",
          }}
          className={
            className ??
            "z-overlay rounded-lg border border-slate-700 bg-slate-900/95 p-2 shadow-xl backdrop-blur-md outline-none"
          }
          {...getFloatingProps(rest)}
        >
          {children}
        </div>
      </FloatingFocusManager>
    </FloatingPortal>
  );
}
